import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'

import {
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent
} from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import {
    AcaoType,
    CAMPO_FONTE_DADOS_TIPO,
    CampoFonteDadosEnum,
    COMPARACAO_TIPO,
    CondicaoRow,
    FONTE_DADOS_TIPO
} from '../regra.types'
import RegraFormScope from '../regra_form.scopes'

const LOG = Logger.get('REGRA-FORM-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    switch: {
        zoom: 0.6
    },
    hbox: {
        display: 'flex',
        flexDirection: 'row'
    },
    vbox: {
        display: 'flex',
        flexDirection: 'column'
    },
    syncButton: {
        alignSelf: 'center'
    }
})

type RegraFormViewProps = {
    className?: string
    scope: RegraFormScope
}
class RegraFormViewClass implements FCClassContext<RegraFormViewProps> {
    // :: Fields
    scope!: RegraFormScope
    sytles!: ReturnType<typeof useStyles>
    condicoesColumns!: GridColDef[]

    // :: emissors

    readonly emitOnSave = () => {
        this.scope.onSave().catch(LOG.caught)
    }

    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly handleAddCondicaoClick = () => {
        this.scope.onAddCondicao().catch(LOG.caught)
    }

    readonly handleEditCondicaoClick = (id: GridRowId) => () => {
        this.scope.idCondicaoSelecionada = id as number
        this.scope.onEditCondicao().catch(LOG.caught)
    }

    readonly handleDeleteCondicaoClick = (id: GridRowId) => () => {
        this.scope.idCondicaoSelecionada = id as number
        this.scope.onDeleteCondicao().catch(LOG.caught)
    }

    #handleAcaoChange = (e: SelectChangeEvent) => {
        this.scope.acao = e.target.value as AcaoType
    }

    // :: Methods

    onSyncState({ scope }: RegraFormViewProps) {
        this.scope = scope
        this.sytles = useStyles()
        this.condicoesColumns = static_build_condicao_columns(
            this.handleEditCondicaoClick,
            this.handleDeleteCondicaoClick
        )
    }

    render({ className, scope }: RegraFormViewProps) {
        this.scope = bindUpdate(React, scope)

        const classes = this.sytles.classes

        return (
            <div className={clsx(classes.view, className)}>
                <Paper elevation={3} style={{ padding: 10 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginBottom: '20px'
                        }}
                    >
                        <FormControl>
                            <InputLabel id="acao-select-label">Ação</InputLabel>
                            <Select
                                labelId="acao-select-label"
                                id="acao-select"
                                value={scope.acao}
                                label="Ação"
                                onChange={this.#handleAcaoChange}
                            >
                                <MenuItem value={'ALERTAR'}>Alertar</MenuItem>
                                <MenuItem value={'BLOQUEAR'}>Bloquear</MenuItem>
                                <MenuItem value={'LIBERAR'}>Liberar</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            className={classes.cnpjInput}
                            value={scope.nome}
                            onChange={(e) => (scope.nome = e.target.value)}
                            label="Nome da regra"
                            variant="standard"
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginBottom: '20px'
                        }}
                    >
                        <TextField
                            className={classes.cnpjInput}
                            value={scope.rating1}
                            onChange={(e) => (scope.rating1 = Number(e.target.value ?? 0))}
                            label="Rating 1"
                            variant="standard"
                        />
                        <TextField
                            className={classes.cnpjInput}
                            value={scope.rating2}
                            onChange={(e) => (scope.rating2 = Number(e.target.value ?? 0))}
                            label="Rating 2"
                            variant="standard"
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginBottom: '20px'
                        }}
                    >
                        <FormGroup className={classes.hbox}>
                            <FormControlLabel
                                required
                                label="Enviar notificação"
                                control={
                                    <Checkbox
                                        checked={scope.enviarNotificacao}
                                        onChange={(e) => (this.scope.enviarNotificacao = e.target.checked)}
                                    />
                                }
                            />
                        </FormGroup>

                        <TextField
                            className={classes.cnpjInput}
                            value={scope.emailsNotificacao}
                            onChange={(e) => (scope.emailsNotificacao = e.target.value)}
                            label="Emails notificação separados por ,"
                            variant="standard"
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginTop: '10px'
                        }}
                    >
                        <Button variant="outlined" onClick={this.handleAddCondicaoClick}>
                            Adicionar Condição
                        </Button>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginTop: '10px',
                            marginBottom: '20px'
                        }}
                    >
                        <DataGrid
                            autoHeight={this.scope.condicoes.length === 0}
                            rows={this.scope.condicoes}
                            columns={this.condicoesColumns}
                            getRowId={static_getCondicaoRowId}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10
                                    }
                                }
                            }}
                        />
                    </Box>

                    <Divider />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginTop: '10px'
                        }}
                    >
                        <Button variant="outlined" onClick={this.emitOnSave}>
                            Salvar
                        </Button>
                        <Button variant="outlined" onClick={this.emitOnClose}>
                            Voltar
                        </Button>
                    </Box>
                </Paper>
            </div>
        )
    }
}

export const RegraFormView = classToFComponent(RegraFormViewClass, React)
export default RegraFormView

function static_build_condicao_columns(
    handleEditClick: (id: GridRowId) => () => void,
    handleDeleteClick: (id: GridRowId) => () => void
): GridColDef[] {
    return [
        {
            field: 'fonteDados',
            headerName: 'Fonte Dados',
            width: 150,
            valueFormatter: (value: string) => {
                return FONTE_DADOS_TIPO.get(value)
            }
        },
        {
            field: 'campoFonteDados',
            headerName: 'Campo',
            width: 200,
            valueGetter: (value, row) => {
                return CAMPO_FONTE_DADOS_TIPO[row.fonteDados].get(value as CampoFonteDadosEnum)
            }
        },
        {
            field: 'comparacao',
            headerName: 'Comparação',
            width: 100,
            valueFormatter: (value: string) => {
                return COMPARACAO_TIPO.get(value)
            }
        },
        {
            field: 'tipo',
            headerName: 'Tipo',
            width: 100
        },
        {
            field: 'valor1',
            headerName: 'Valor 1',
            width: 100
        },
        {
            field: 'valor2',
            headerName: 'Valor 2',
            width: 100
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 80,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        key={'edit' + id}
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={'del' + id}
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                ]
            }
        }
    ]
}

function static_getCondicaoRowId(row: CondicaoRow) {
    return row.id + ''
}
